import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingService } from '../../services/bookingService';
import { notifyPhotographerNewBooking } from '../../services/notificationService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';
import { formatNumber } from '../../utils/formatNumber';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
import { getExperienceLevelLabelAr } from '../../utils/experienceLevel';
import AlternativePhotographersPanel from './components/AlternativePhotographersPanel';

const to12hTime = (timeStr) => {
  if (!timeStr) return '';
  const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return timeStr;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const isPM = h >= 12;
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${min} ${isPM ? 'م' : 'ص'}`;
};

const getSessionLocationLabel = (booking) => {
  const loc = booking.locationDetails || booking.location_details;
  if (loc) {
    try {
      const parsed = typeof loc === 'string' ? JSON.parse(loc) : loc;
      if (parsed?.address_text) return parsed.address_text;
      const parts = [parsed?.city, parsed?.district].filter(Boolean);
      if (parts.length) return parts.join('، ');
    } catch (_) { /* ignore */ }
  }
  return booking.city || 'غير محدد';
};

const BookingWaiting = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectingPhotographerId, setSelectingPhotographerId] = useState(null);
  const previousPhotographerIdRef = useRef(null);
  const whatsappSentRef = useRef(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (bookingId) {
      loadBooking();
      pollingRef.current = setInterval(() => loadBooking(true), 3000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [bookingId]);

  const loadBooking = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await bookingService.getBookingById(bookingId);

      const currentPhotographerId = data?.assignedPhotographerId || data?.assigned_photographer_id;
      const previousPhotographerId = previousPhotographerIdRef.current;

      if (
        currentPhotographerId && previousPhotographerId !== null &&
        previousPhotographerId !== undefined &&
        currentPhotographerId !== previousPhotographerId && !whatsappSentRef.current
      ) {
        try {
          const { whatsappService } = await import('../../services/whatsappService');
          await whatsappService.notifyPhotographerNewBooking(currentPhotographerId, bookingId);
          whatsappSentRef.current = true;
        } catch (_) {}
        try {
          await notifyPhotographerNewBooking(currentPhotographerId, bookingId);
        } catch (_) {}
      }

      previousPhotographerIdRef.current = currentPhotographerId;
      setBooking(data);

      if (data?.holdExpiresAt || data?.hold_expires_at) {
        updateTimeRemaining(data.holdExpiresAt || data.hold_expires_at);
      } else if (data?.status === 'pending_photographer_confirmation') {
        updateTimeRemaining(new Date(Date.now() + 3 * 60 * 1000));
      }

      if (data?.status === 'awaiting_payment') navigate(`/booking-payment/${bookingId}`);
      else if (data?.status === 'cancelled_by_customer' || data?.status === 'cancelled_by_admin' || data?.status === 'cancelled')
        navigate('/customer-dashboard/bookings');
      else if (data?.status === 'declined') {
        if (currentPhotographerId !== previousPhotographerId) whatsappSentRef.current = false;
      }
      // 'expired' → stay on page, show alternatives
    } catch (_) {
      console.error('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const updateTimeRemaining = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) { setTimeRemaining({ minutes: 0, seconds: 0, expired: true }); return; }
    setTimeRemaining({ minutes: Math.floor(diff / 60000), seconds: Math.floor((diff % 60000) / 1000), expired: false });
  };

  useEffect(() => {
    if (!timeRemaining || timeRemaining.expired) return;
    const interval = setInterval(() => {
      if (booking?.holdExpiresAt || booking?.hold_expires_at)
        updateTimeRemaining(booking.holdExpiresAt || booking.hold_expires_at);
    }, 1000);
    return () => clearInterval(interval);
  }, [booking?.holdExpiresAt, booking?.hold_expires_at, timeRemaining?.expired]);

  const handleCancelBooking = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;
    try {
      setCancelling(true);
      await bookingService.updateBookingStatus(bookingId, 'cancelled_by_customer');
      navigate('/customer-dashboard/bookings');
    } catch (_) { alert('فشل إلغاء الحجز.'); } finally { setCancelling(false); }
  };

  const handleRetry = async () => {
    try {
      setChecking(true);
      whatsappSentRef.current = false;
      previousPhotographerIdRef.current = null;
      await bookingService.retryBooking(bookingId);
      await loadBooking();
    } catch (_) { alert('فشل إعادة المحاولة.'); } finally { setChecking(false); }
  };

  const handleSelectPhotographer = async (photographerId) => {
    try {
      setSelectingPhotographerId(photographerId);
      whatsappSentRef.current = false;
      previousPhotographerIdRef.current = null;
      await bookingService.retryBookingWithPhotographer(bookingId, photographerId);
      await loadBooking();
    } catch (_) { alert('فشل اختيار المصور.'); } finally { setSelectingPhotographerId(null); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-slate-600">جاري التحميل...</p>
      </div>
    </div>
  );

  if (!booking) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
      <div className="text-center">
        <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">الحجز غير موجود</h2>
        <Button onClick={() => navigate('/customer-dashboard/bookings')} iconName="ArrowRight">العودة إلى الحجوزات</Button>
      </div>
    </div>
  );

  const shouldShowAlternatives =
    booking.status === 'declined' ||
    booking.status === 'expired' ||
    booking.status === 'cancelled_by_photographer' ||
    (booking.status === 'pending_photographer_confirmation' && timeRemaining?.expired);

  const canRetry =
    booking.status === 'declined' ||
    booking.status === 'expired' ||
    booking.status === 'cancelled_by_photographer' ||
    (booking.status === 'pending_photographer_confirmation' && timeRemaining?.expired && booking.attemptCount < 3);

  const alternativesReason = (booking.status === 'declined' || booking.status === 'cancelled_by_photographer') ? 'declined' : 'expired';

  const photographer = booking.assignedPhotographer || booking.photographer || null;
  const hasPhotographer = !!(booking.assignedPhotographerId || booking.assigned_photographer_id);

  const assignedExperienceLabel = (() => {
    const snap = booking.pricingSnapshot || booking.pricing_snapshot;
    const raw =
      photographer?.experienceLevel ||
      photographer?.experience_level ||
      snap?.experienceLevel ||
      booking.experienceLevel ||
      booking.experience_level;
    return getExperienceLevelLabelAr(raw, 'certified');
  })();

  // Countdown SVG
  const r = 45, circumference = 2 * Math.PI * r;
  const progress = timeRemaining && !timeRemaining.expired && !shouldShowAlternatives
    ? (timeRemaining.minutes * 60 + timeRemaining.seconds) / (3 * 60) : 0;
  const CountdownSvg = ({ size }) => (
    <svg width={size} height={size} className="transform -rotate-90" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="4" className="text-slate-200" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
        className="text-primary transition-all duration-1000 ease-linear"
        strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progress)} />
    </svg>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="mb-4">
            <BackButton to="/customer-dashboard/bookings" label="العودة إلى الحجوزات" />
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-10 text-center">

            {/* ── صورة المصور + العداد (مشترك دائماً) ── */}
            <div className="flex flex-col items-center gap-3 mb-6">
              {hasPhotographer && photographer ? (
                <div className="relative w-20 h-20">
                  <img
                    src={photographer.avatar_url || photographer.avatarUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'}
                    alt={photographer.full_name || photographer.fullName || 'مصور'}
                    className={`w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg ${shouldShowAlternatives ? 'opacity-40 grayscale' : ''}`}
                  />
                  {!shouldShowAlternatives && timeRemaining && !timeRemaining.expired && (
                    <div className="absolute inset-0"><CountdownSvg size={80} /></div>
                  )}
                  {shouldShowAlternatives && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center border-2 border-white">
                      <Icon name="X" size={12} className="text-white" />
                    </div>
                  )}
                </div>
              ) : (
                !shouldShowAlternatives && timeRemaining && !timeRemaining.expired && (
                  <div className="relative w-24 h-24">
                    <CountdownSvg size={96} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-2xl font-bold text-primary tabular-nums">
                        {timeRemaining.minutes}:{String(timeRemaining.seconds).padStart(2, '0')}
                      </p>
                    </div>
                  </div>
                )
              )}

              {/* اسم المصور + عداد رقمي (انتظار فقط) */}
              {hasPhotographer && photographer && (
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">
                    {shouldShowAlternatives ? 'اعتذر عن الحجز' : 'المصور المحدد'}
                  </p>
                  <p className="font-bold text-slate-900">{photographer.full_name || photographer.fullName || 'مصور'}</p>
                  {!shouldShowAlternatives && (
                    <p className="text-xs text-slate-500 mt-0.5">مستوى الخبرة: {assignedExperienceLabel}</p>
                  )}
                  {!shouldShowAlternatives && timeRemaining && !timeRemaining.expired && (
                    <p className="text-2xl font-bold text-primary mt-1 tabular-nums">
                      {timeRemaining.minutes}:{String(timeRemaining.seconds).padStart(2, '0')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── العنوان والرسالة ── */}
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {shouldShowAlternatives ? 'اختر مصوراً بديلاً' : 'طلبك قيد التأكيد'}
            </h1>
            <p className="text-slate-500 mb-5 text-sm">
              {shouldShowAlternatives
                ? booking.status === 'declined'
                  ? 'اعتذر المصور المحدد عن الحجز — اختر مصوراً من القائمة أدناه'
                  : 'لم يرد المصور في الوقت المحدد او انه غير متاح — اختر مصوراً بنفس المستوى من القائمة أدناه'
                : hasPhotographer
                  ? 'جاري انتظار رد المصور على طلبك'
                  : 'جاري التحقق من توفر المصور'}
            </p>

            {/* رسالة الطمأنينة (انتظار فقط) */}
            {!shouldShowAlternatives && booking.status === 'pending_photographer_confirmation' && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full mb-6">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                <p className="text-sm text-green-700 font-medium">عادةً يتم الرد خلال دقائق</p>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════
                المنطقة المركزية:
                - انتظار  → "مهم جداً"
                - رفض/انتهاء → بطاقات المصورين البديلين
               ══════════════════════════════════════════════════════ */}

            {!shouldShowAlternatives ? (
              /* مهم جداً */
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 mb-6 text-right">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700">
                    لن يتم خصم أي مبلغ قبل تأكيد توفر المصور. سيتم إشعارك فوراً عند التأكيد.
                  </p>
                </div>
              </div>
            ) : (
              /* بطاقات المصورين البديلين */
              <div className="mb-6 text-right">
                {/* زر البحث التلقائي كخيار ثانوي */}
                {canRetry && (
                  <div className="flex items-center gap-2 mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetry}
                      loading={checking}
                      iconName="RefreshCw"
                      iconPosition="right"
                      className="text-slate-500 border-slate-200 text-xs"
                    >
                      بحث تلقائي
                    </Button>
                    <p className="text-xs text-slate-400">أو اختر مصوراً بنفسك</p>
                  </div>
                )}
                <AlternativePhotographersPanel
                  booking={booking}
                  onSelectPhotographer={handleSelectPhotographer}
                  selectingId={selectingPhotographerId}
                  reason={alternativesReason}
                />
              </div>
            )}

            {/* ── ملخص الطلب ── */}
            <div className="bg-slate-50 rounded-xl p-5 mb-6 text-right">
              <h3 className="font-semibold text-slate-900 mb-3 text-sm">ملخص الطلب</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500 flex-shrink-0">نوع الخدمة</span>
                  <span className="font-medium text-slate-900 text-left">{getPhotographyTypeLabelAr(booking.photographyType || booking.serviceType)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500 flex-shrink-0">الموقع</span>
                  <span className="font-medium text-slate-900 text-left">{getSessionLocationLabel(booking)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500 flex-shrink-0">التاريخ والوقت</span>
                  <span className="font-medium text-slate-900 text-left">
                    {booking.bookingDate
                      ? new Date(booking.bookingDate).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })
                      : 'غير محدد'}{' — '}
                    {to12hTime(booking.bookingTime || booking.time) || 'غير محدد'}
                  </span>
                </div>
                <div className="flex justify-between gap-4 border-t border-slate-200 pt-2.5 mt-1">
                  <span className="text-slate-500 flex-shrink-0">السعر</span>
                  <span className="font-bold text-primary">{formatNumber(booking.finalPrice || booking.price || 0)} ر.س</span>
                </div>
              </div>
            </div>

            {/* ── الأزرار ── */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/customer-dashboard/bookings')} iconName="ArrowRight">
                العودة إلى الحجوزات
              </Button>
              {/* زر "تفاصيل الحجز المنتهي" مخفي */}
              {(booking.status === 'pending_photographer_confirmation' || booking.status === 'pending') && (
                <Button variant="outline" onClick={handleCancelBooking} loading={cancelling} disabled={cancelling} className="text-error border-error hover:bg-error/10" iconName="X">
                  إلغاء الطلب
                </Button>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingWaiting;
